import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Alert, Box, CircularProgress, Link as MuiLink, Typography } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import LuxLoyaltyAccountChip from '~/components/LuxLoyalty/LuxLoyaltyAccountChip';

import { getLuxLoyaltyAccount, luxLoyaltyRegions } from '~/services/LuxLoyaltyService';
import UsersService from '~/services/UsersService';

import { SectionCard } from './PayloadBuilder/SectionCard';

const regions = libRegions.getRegions();
const regionsByCode = Object.fromEntries(regions.map((region) => [region.code, region]));

function formatPhone(phonePrefix: unknown, phone: unknown): string {
  const prefix = typeof phonePrefix === 'string' ? phonePrefix.trim() : '';
  const number = typeof phone === 'string' ? phone.trim() : phone != null ? String(phone) : '';
  if (!prefix && !number) return '—';
  return [prefix, number].filter(Boolean).join(' ');
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  );
}

interface Props {
  customerId: string;
}

export default function EvidenceReviewUserDetails({ customerId }: Props) {
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useQuery({
    queryKey: ['users', customerId, 'evidence-review'],
    queryFn: ({ signal }) => UsersService.getUser(customerId, { signal }),
    enabled: !!customerId,
    retry: false,
  });

  const {
    data: luxLoyaltyAccount,
    isLoading: isLoadingLoyalty,
    error: loyaltyError,
  } = useQuery({
    queryKey: ['lux-loyalty', 'account', customerId, 'evidence-review'],
    queryFn: () => getLuxLoyaltyAccount(customerId),
    enabled: !!customerId,
    retry: false,
  });

  const userCountryName = user?.country_code ? (regionsByCode[user.country_code]?.name ?? user.country_code) : '—';
  const societeRegionName = luxLoyaltyAccount?.region
    ? (luxLoyaltyRegions[luxLoyaltyAccount.region as keyof typeof luxLoyaltyRegions] ?? luxLoyaltyAccount.region)
    : '—';

  return (
    <SectionCard title="User details" subtitle="Customer profile and Société membership." defaultExpanded>
      {isLoadingLoyalty ? (
        <Box display="flex" justifyContent="flex-start" py={0.5}>
          <CircularProgress size={20} />
        </Box>
      ) : luxLoyaltyAccount ? (
        <Box sx={{ alignSelf: 'flex-start' }}>
          <LuxLoyaltyAccountChip luxLoyaltyAccount={luxLoyaltyAccount} />
        </Box>
      ) : loyaltyError ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          Could not load Société membership.
        </Alert>
      ) : null}

      <DetailRow label="Customer BK">
        <MuiLink
          component={Link}
          to={`/users/${customerId}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ fontFamily: 'monospace' }}
        >
          {customerId}
        </MuiLink>
      </DetailRow>

      {isLoadingUser ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : userError ? (
        <Alert severity="warning">
          Could not load user profile. Customer BK and Société details above may still be valid.
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            columnGap: 3,
            rowGap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          }}
        >
          <DetailRow label="First name">{user?.givenName || '—'}</DetailRow>
          <DetailRow label="Last name">{user?.surname || '—'}</DetailRow>
          <DetailRow label="Phone">{formatPhone(user?.phone_prefix, user?.phone)}</DetailRow>
          <DetailRow label="Email">{user?.email || '—'}</DetailRow>
          <DetailRow label="Region country">{userCountryName}</DetailRow>
          <DetailRow label="Société region">{societeRegionName}</DetailRow>
        </Box>
      )}
    </SectionCard>
  );
}
